import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui-kit/accordion';
import { Switch } from '@/components/ui-kit/switch';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui-kit/form';
import { InfoTooltip } from '@/components/ui-kit/info-tool-tip';
import { ChipsInput, ChipsInputField, ChipsInputList } from '@/components/chip-input/chips-input';
import { AgentBehaviorSettingsForm } from '@/modules/gpt-chats/components/agents-configuration/components/config-card-section/utils';
import { CustomRulesList } from './custom-rules-list';
import { useState } from 'react';
import { RiskThresholdModal } from './risk-threshold-modal';
import { Label } from '@/components/ui-kit/label';
import useIsMobile from '@/hooks/use-is-mobile';

type SafetyMechanismSectionProps = {
  form: UseFormReturn<AgentBehaviorSettingsForm>;
  isMobile: boolean;
  onModelSubmit: (values: AgentBehaviorSettingsForm) => Promise<void>;
};

export const SafetyMechanismSection = ({
  form,
  isMobile,
  onModelSubmit,
}: SafetyMechanismSectionProps) => {
  const { t } = useTranslation();
  const isXlBreakpoint = useIsMobile();

  const { control } = form;
  const [isRiskThresholdModalOpen, setIsRiskThresholdModalOpen] = useState(false);

  const inputRiskThreshold = form.watch('guardrail.input_risk_threshold');
  const outputRiskThreshold = form.watch('guardrail.output_risk_threshold');

  const isValidRegex = (pattern: string): boolean => {
    try {
      new RegExp(pattern);
      return true;
    } catch (_) {
      return false;
    }
  };

  const isRegexPattern = (pattern: string): boolean => {
    // const errorMsg =
    //   'Please enter a valid regex pattern with special characters (e.g., .*, ^start, end$)';
    if (!isValidRegex(pattern)) {
      return false;
    }
    if (pattern.trim() === t('REGEX_VALIDATION_ERROR')) {
      return false;
    }
    const regexIndicators = /[.*+?^${}()|[\]\\]/;
    return regexIndicators.test(pattern);
  };

  const handleEnableGuardrailChange = async (val: boolean) => {
    form.setValue('enable_guardrails', val);
    if (onModelSubmit) {
      await onModelSubmit(form.getValues());
    }
  };

  const handleValidationToggle = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    field: import('react-hook-form').ControllerRenderProps<AgentBehaviorSettingsForm, any>,
    val: boolean,
    fieldName: string
  ) => {
    field.onChange(val);
    form.setValue(fieldName as keyof AgentBehaviorSettingsForm, !!val);

    const preVal =
      fieldName === 'guardrail.enable_pre_validation'
        ? val
        : form.getValues('guardrail.enable_pre_validation');
    const postVal =
      fieldName === 'guardrail.enable_post_validation'
        ? val
        : form.getValues('guardrail.enable_post_validation');
    const shouldEnableGuardrails = preVal || postVal;

    form.setValue('enable_guardrails', shouldEnableGuardrails, { shouldDirty: false });

    await onModelSubmit(form.getValues());
  };

  const saveGuardrail = async () => {
    const values = form.getValues();
    await onModelSubmit(values);
  };

  return (
    <AccordionItem value="safety" className="border-b-0">
      <div className="relative">
        <AccordionTrigger className="px-5 text-lg hover:no-underline">
          <div className="flex w-full items-center justify-between pr-4">
            <Label className="text-lg">{t('GUARDRAILS')}</Label>
          </div>
        </AccordionTrigger>

        <div className="absolute right-12 top-1/2 z-10 -translate-y-1/2">
          <Switch
            id="safety-mechanism-toggle"
            checked={form.watch('enable_guardrails')}
            onCheckedChange={handleEnableGuardrailChange}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            // size="sm"
          />
        </div>
      </div>
      <AccordionContent className="w-full px-5">
        <div className={`flex w-full flex-col gap-6`}>
          <div className="grid gap-10">
            <div className="flex flex-col gap-4">
              <span className="text-sm font-semibold">{t('VALIDATION_STAGES')}</span>
              <div className={`grid gap-10 ${isXlBreakpoint ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <FormLabel>{t('USER_QUERY_VALIDATION')}</FormLabel>
                    <InfoTooltip content={t('USER_QUERY_VALIDATION_TOOLTIP')} />
                  </div>
                  <FormField
                    control={control}
                    name="guardrail.enable_pre_validation"
                    render={({ field }) => (
                      <Switch
                        checked={field.value ?? true}
                        onCheckedChange={(val) =>
                          handleValidationToggle(field, val, 'guardrail.enable_pre_validation')
                        }
                      />
                    )}
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <FormLabel>{t('AGENT_RESPONSE_VALIDATION')}</FormLabel>
                    <InfoTooltip content={t('AGENT_RESPONSE_VALIDATION_TOOLTIP')} />
                  </div>
                  <FormField
                    control={control}
                    name="guardrail.enable_post_validation"
                    render={({ field }) => (
                      <Switch
                        checked={field.value ?? true}
                        onCheckedChange={(val) =>
                          handleValidationToggle(field, val, 'guardrail.enable_post_validation')
                        }
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="mb-1 flex flex-col gap-4">
              <span className="text-sm font-semibold">{t('SAFETY_CHECKS')}</span>
              <div className={`grid gap-10 ${isXlBreakpoint ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <FormLabel>{t('PII_DETECTION')}</FormLabel>
                    <InfoTooltip content={t('PII_DETECTION_TOOLTIP')} />
                  </div>
                  <FormField
                    control={control}
                    name="guardrail.enable_pii_detection"
                    render={({ field }) => (
                      <Switch
                        checked={!!field.value}
                        onCheckedChange={async (val) => {
                          field.onChange(!!val);
                          await onModelSubmit(form.getValues());
                        }}
                      />
                    )}
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <FormLabel>{t('INJECTION_DETECTION')}</FormLabel>
                    <InfoTooltip content={t('INJECTION_DETECTION_TOOLTIP')} />
                  </div>
                  <FormField
                    control={control}
                    name="guardrail.enable_injection_detection"
                    render={({ field }) => (
                      <Switch
                        checked={!!field.value}
                        onCheckedChange={async (val) => {
                          field.onChange(!!val);
                          await onModelSubmit(form.getValues());
                        }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6">
            <div className="relative w-full">
              <div className="mb-3 text-sm font-semibold">{t('RISK_THRESHOLDS')}</div>
              <div className="flex flex-wrap gap-4" id="risk-threshold-buttons">
                <div
                  className="w-fit cursor-pointer rounded-lg bg-secondary px-3 py-2 transition-colors hover:bg-primary/10"
                  onClick={() => setIsRiskThresholdModalOpen(true)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-high-emphasis">{t('INPUT_RISK')}:</span>
                    <span className="text-sm font-semibold text-primary">
                      {inputRiskThreshold?.toFixed(2) || '0.70'}
                    </span>
                  </div>
                </div>

                <div
                  className="w-fit cursor-pointer rounded-lg bg-secondary px-3 py-2 transition-colors hover:bg-primary/10"
                  onClick={() => setIsRiskThresholdModalOpen(true)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-high-emphasis">{t('OUTPUT_RISK')}:</span>
                    <span className="text-sm font-semibold text-primary">
                      {outputRiskThreshold?.toFixed(2) || '0.80'}
                    </span>
                  </div>
                </div>
              </div>

              {isRiskThresholdModalOpen && (
                <RiskThresholdModal
                  form={form}
                  onClose={() => setIsRiskThresholdModalOpen(false)}
                  isMobile={isMobile}
                />
              )}
            </div>
          </div>

          <div className="grid gap-6">
            <span className="text-sm font-semibold">{t('BLOCKED_CONTENT')}</span>
            <div className={`grid gap-6 ${isXlBreakpoint ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <FormLabel>{t('KEYWORD_CHECK')}</FormLabel>
                  <InfoTooltip content={t('KEYWORD_CHECK_TOOLTIP')} />
                </div>
                <FormField
                  control={control}
                  name="guardrail.enable_keyword_check"
                  render={({ field }) => (
                    <Switch
                      checked={!!field.value}
                      onCheckedChange={async (val) => {
                        field.onChange(!!val);
                        await onModelSubmit(form.getValues());
                      }}
                    />
                  )}
                />
              </div>
            </div>
            {form.watch('guardrail.enable_keyword_check') && (
              <div className={`grid gap-10 ${isXlBreakpoint ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <FormField
                  control={form.control}
                  name="guardrail.banned_keywords"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        {t('SENSITIVE_KEYWORDS')}
                        <InfoTooltip content={t('SENSITIVE_KEYWORDS_TOOLTIP')} />
                      </FormLabel>
                      <FormControl>
                        <ChipsInput
                          value={field.value || []}
                          onChange={async (val: string[]) => {
                            field.onChange(val);
                            form.setValue('guardrail.banned_keywords', val);
                            await onModelSubmit(form.getValues());
                          }}
                          className="h-32 overflow-y-auto"
                        >
                          <ChipsInputList />
                          <ChipsInputField />
                        </ChipsInput>
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="guardrail.banned_patterns"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        {t('BANNED_PATTERNS')}
                        <InfoTooltip content={t('BANNED_PATTERNS_TOOLTIP')} />
                      </FormLabel>
                      <FormControl>
                        <ChipsInput
                          value={field.value || []}
                          onChange={async (val: string[]) => {
                            field.onChange(val);
                            form.setValue('guardrail.banned_patterns', val);
                            await onModelSubmit(form.getValues());
                          }}
                          customValidator={isRegexPattern}
                          validatorRegexErrorMessage={t('REGEX_VALIDATION_ERROR')}
                          className="h-32 overflow-y-auto"
                        >
                          <ChipsInputList />
                          <ChipsInputField />
                        </ChipsInput>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 border-t pt-8">
          <div className={`grid gap-6 ${isXlBreakpoint ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <FormLabel className="text-sm font-semibold">{t('CUSTOM_RULES')}</FormLabel>
                <InfoTooltip content={t('CUSTOM_RULES_TOOLTIP')} />
              </div>
              <FormField
                control={control}
                name="guardrail.enable_custom_rules"
                render={({ field }) => (
                  <Switch
                    // size="sm"
                    checked={!!field.value}
                    onCheckedChange={async (val) => {
                      field.onChange(val);
                      form.setValue('guardrail.enable_custom_rules', !!val);
                      await onModelSubmit(form.getValues());
                    }}
                  />
                )}
              />
            </div>
          </div>
          <CustomRulesList form={form} onSave={saveGuardrail} isMobile={isMobile} />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
